import React from "react";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa";
import { RiExternalLinkLine } from "react-icons/ri";
import clsx from "clsx";
import { useAuth0 } from "@auth0/auth0-react";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import useOneTapAuth from "@/hooks/useOneTapAuth";
import GuestAuthPromptModal from "@/components/common/GuestAuthPromptModal";
import useAgentFollow from "@/hooks/useAgentFollow";

type Agent = {
    userId?: string | null;
    name?: string;
    company?: string;
    profilePicture?: string;
    profileUrl?: string;
    mlsId?: string;
    nickname?: string;
    isFollowing?: boolean;
};

type AgentCardProps = {
    agent?: Agent;
    clickLocation: "property_page" | "daily_score_modal";
    followInvalidateQueryKeys?: ReadonlyArray<readonly unknown[]>;
};

/**
 * AgentCard component displays agent information with profile picture, name, company, and social media links
 * @param {Object} props - Component props
 * @param {Object} props.agent - Agent data object
 * @param {string} props.agent.name - Agent's full name
 * @param {string} props.agent.company - Agent's company name
 * @param {string} props.agent.profilePicture - URL to agent's profile picture
 * @param {string} props.agent.profileUrl - URL to agent's profile page
 * @param {string} props.agent.mlsId - Agent's MLS ID
 * @param {string} props.clickLocation - The location where the AgentCard is displayed (property_page, daily_score_modal)
 * @returns {JSX.Element} AgentCard component
 */
const AgentCard: React.FC<AgentCardProps> = ({ agent, clickLocation, followInvalidateQueryKeys }) => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [showGuestAuthPrompt, setShowGuestAuthPrompt] = React.useState(false);
    const { handleGoogle, handleEmail } = useOneTapAuth({
        redirectPath: `/profile`,
        version: "puzzle",
        onClose: () => setShowGuestAuthPrompt(false),
        location: "Google 1-Tap Modal", // location — Register event
    });

    const { userId, name, company, profilePicture, profileUrl, mlsId, nickname, isFollowing: initialIsFollowing } =
        agent || {};
    const followerUserId = userState?.id ? String(userState.id) : null;
    const followedUserId = userId ? String(userId) : null;
    const { isFollowing, isPending: isFollowPending, toggleFollow } = useAgentFollow({
        followerUserId,
        followedUserId,
        initialIsFollowing,
        isAuthenticated,
        onRequireAuth: () => setShowGuestAuthPrompt(true),
        mixpanelLocation: "Puzzle Page",
        nicknameFollowed: nickname ?? null,
        invalidateQueryKeys: followInvalidateQueryKeys,
    });

    const trackAgentClick = (clickType: string) => {
        const eventData = {
            isAuthenticated,
            pageUrl: window.location.href,
            user_id: userState?.id || null,
            nickname: userState?.nickname || null,
            email: userState?.emailAddress || null,
            agentName: name,
            agentMlsId: mlsId,
            clickType,
            clickLocation,
        };

        sendMixpanelEvent("Agent Click", eventData);
    };

    const handleExternalLinkClick = (url: string) => {
        if (!url) return;

        trackAgentClick("external_link");
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleCardClick = (e: React.MouseEvent) => {
        // Don't trigger if clicking on the external link button
        if ((e.target as Element).closest(".external-link-btn, .follow-btn")) {
            return;
        }

        if (nickname) {
            trackAgentClick("custom");
            navigate(`/profile/${encodeURIComponent(nickname)}`);
            return;
        }
    };

    const handleFollowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        toggleFollow();
    };

    return (
        <>
            <div
                className={clsx(
                    "tw-bg-white tw-rounded-xl tw-p-5 tw-mx-0 tw-shadow-md tw-w-full tw-font-noto tw-min-h-fit",
                    { "tw-cursor-pointer": Boolean(nickname) },
                )}
                onClick={handleCardClick}
            >
            {/* Header Section */}
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                <span className="tw-font-bold tw-text-lg tw-text-gray-800 tw-font-merriweather">
                    Listing courtesy of:
                </span>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {profileUrl && (
                        <button
                            className="external-link-btn tw-bg-transparent tw-border-none tw-text-gray-500 tw-cursor-pointer tw-p-1 tw-rounded tw-transition-colors hover:tw-text-gray-800"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleExternalLinkClick(profileUrl);
                            }}
                            aria-label="View agent profile"
                        >
                            <RiExternalLinkLine className="tw-w-5 tw-h-5" />
                        </button>
                    )}
                    {followedUserId ? (
                        <button
                            type="button"
                            className={clsx(
                                "follow-btn tw-rounded-full tw-px-4 tw-py-1 tw-text-sm tw-font-semibold tw-transition-colors",
                                isFollowing
                                    ? "tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-gray-300"
                                    : "tw-bg-[#F69929] tw-text-white hover:tw-bg-[#e78a1c]",
                            )}
                            disabled={isFollowPending}
                            onClick={handleFollowClick}
                        >
                            {isFollowing ? "Following" : "Follow"}
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Profile Information Section */}
            <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-relative tw-w-12 tw-h-12 tw-rounded-full tw-overflow-hidden tw-flex-shrink-0">
                    {profilePicture ? (
                        <img
                            src={profilePicture}
                            alt={`${name} profile`}
                            className="tw-w-full tw-h-full tw-object-cover tw-rounded-full"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                                const nextSibling = target.nextSibling as HTMLElement;
                                if (nextSibling) {
                                    nextSibling.style.display = "flex";
                                }
                            }}
                        />
                    ) : null}
                    <div
                        className={clsx(
                            "tw-w-full tw-h-full tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-text-lg",
                            { "tw-hidden": profilePicture },
                        )}
                    >
                        <FaUser />
                    </div>
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                    <h3 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-m-0 tw-mb-1 tw-leading-tight">{name}</h3>
                    <p className="tw-text-sm tw-text-gray-600 tw-m-0 tw-leading-tight">{company}</p>
                </div>
            </div>

            </div>
            <GuestAuthPromptModal
                show={showGuestAuthPrompt}
                onClose={() => setShowGuestAuthPrompt(false)}
                onGoogle={handleGoogle}
                onEmail={handleEmail}
                description="Sign in to follow this agent and get updates" // description
                source="follow" // source
            />
        </>
    );
};

export default AgentCard;
